import { ArrowRight, FileText } from "lucide-react";
import type { BlogPost, Industry, Product, Project, Service, Solution } from "@latam-energy/types";
import { AppImage as Image } from "./AppImage";
import { AppLink as Link } from "./AppLink";
import { formatDate } from "@/lib/utils";

export function ServiceCard({ service }: { service: Service }) {
  return (
    <article className="card flex h-full flex-col overflow-hidden transition duration-200 hover:-translate-y-1 hover:border-cyan hover:shadow-industrial">
      <div className="relative h-52 overflow-hidden">
        <Image src={serviceImage(service.slug)} alt={`Servicio de ${service.title}`} width={720} height={420} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-xl font-extrabold text-white">{service.title}</h3>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="leading-7 text-slate-600">{service.summary}</p>
        <Link className="mt-auto inline-flex items-center gap-2 pt-5 font-extrabold text-navy hover:text-cyan" href={`/servicios/${service.slug}`}>
          Ver servicio <ArrowRight size={18} aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

export function ProductCard({ product }: { product: Product }) {
  const getImagePositionClass = (slug: string) => {
    if (slug === "flujometro-electromagnetico") return "object-position-top";
    if (slug === "transmisor-presion-proceso") return "object-position-center-higher";
    if (slug === "termocupla-industrial") return "object-position-top";
    if (slug === "valvula-control") return "object-position-top";
    return "";
  };
  
  return (
    <article className="card flex h-full flex-col overflow-hidden transition duration-200 hover:-translate-y-1 hover:border-cyan hover:shadow-industrial">
      {product.slug === "valvula-mariposa" ? (
        <div className="aspect-[4/3] w-full bg-slate-100 flex items-center justify-center gap-2 p-2">
          <Image src="/assets/industrial/mariposa-valve.png" alt="Válvula mariposa" width={360} height={260} className="h-full w-1/2 object-contain" />
          <Image src="/assets/industrial/valvula-pp-2.png" alt="Válvula PPH" width={360} height={260} className="h-full w-1/2 object-contain" />
        </div>
      ) : (
        <Image src={product.mainImage} alt={`Imagen referencial de ${product.name}`} width={720} height={520} className={`aspect-[4/3] w-full bg-slate-100 object-cover ${getImagePositionClass(product.slug)}`} />
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs font-extrabold uppercase text-cyan">{product.category}</p>
          {product.featured ? <span className="badge badge--success text-[0.68rem]">Destacado</span> : null}
        </div>
        <h3 className="mt-2 text-lg font-extrabold text-navy">{product.name}</h3>
        <p className="mt-2 text-sm font-semibold text-slate-600">{product.brand} · {product.model}</p>
        <p className="mt-3 leading-7 text-slate-600">{product.shortDescription}</p>
        <div className="mt-auto grid grid-cols-2 gap-2 pt-5">
          <Link className="btn btn-outline w-full px-3 text-center" href={`/productos/${product.slug}`}>Ver detalles</Link>
          <Link className="btn btn-primary w-full px-3 text-center" href={`/cotizar?producto=${product.slug}`}>Cotizar</Link>
        </div>
      </div>
    </article>
  );
}

export function IndustryCard({ industry }: { industry: Industry }) {
  return (
    <Link className="card block p-6 hover:border-cyan" href={`/industrias/${industry.slug}`}>
      <h3 className="text-xl font-extrabold text-navy">{industry.name}</h3>
      <p className="mt-3 leading-7 text-slate-600">{industry.summary}</p>
    </Link>
  );
}

export function SolutionCard({ solution }: { solution: Solution }) {
  return (
    <Link className="card block p-6 hover:border-cyan" href={`/soluciones/${solution.slug}`}>
      <h3 className="text-xl font-extrabold text-navy">{solution.title}</h3>
      <p className="mt-3 leading-7 text-slate-600">{solution.summary}</p>
    </Link>
  );
}

export function ProjectCard({ project }: { project: Project }) {
  return (
    <Link className="card block p-6 hover:border-cyan" href={`/proyectos/${project.slug}`}>
      <p className="text-xs font-extrabold uppercase text-cyan">{project.industry}</p>
      <h3 className="mt-2 text-xl font-extrabold text-navy">{project.title}</h3>
      <p className="mt-3 leading-7 text-slate-600">{project.summary}</p>
    </Link>
  );
}

export function BlogCard({ post }: { post: BlogPost }) {
  return (
    <article className="card p-6">
      <p className="flex items-center gap-2 text-xs font-extrabold uppercase text-cyan"><FileText size={16} aria-hidden="true" /> {post.category} · {formatDate(post.date)} · {post.readingTime}</p>
      <h3 className="mt-3 text-xl font-extrabold text-navy">{post.title}</h3>
      <p className="mt-3 leading-7 text-slate-600">{post.summary}</p>
      <Link className="mt-5 inline-flex items-center gap-2 font-extrabold text-steel hover:text-cyan" href={`/blog/${post.slug}`}>
        Leer artículo <ArrowRight size={18} aria-hidden="true" />
      </Link>
    </article>
  );
}

function serviceImage(slug: string) {
  if (slug.includes("automatizacion") || slug.includes("integracion")) return "/assets/industrial/automation-controls.jpg";
  if (slug.includes("electrica")) return "/assets/industrial/elevator-service.jpg";
  if (slug.includes("eficiencia")) return "/assets/industrial/motor-engine.jpg";
  if (slug.includes("instrumentacion") || slug.includes("mantenimiento")) return "/assets/industrial/control-valve.jpg";
  return "/assets/industrial/hero-bg.jpg";
}
